/* RESPONSIBLE TEAM: team-automation-foundations */
import Route from '@ember/routing/route';
import { inject as service } from '@ember/service';
import { hash } from 'rsvp';
import { get } from 'embercom/lib/ajax';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import { EntityType } from 'embercom/models/data/entity-types';

export default class AppsAppOperatorRoute extends Route {
  @service store;
  @service appService;
  @service router;
  @service contentImportService;
  @service reactTeammateAppIsland;
  @service knowledgeHubDrawerEditorService;

  get app() {
    return this.appService.app;
  }

  get isNewOperatorEnabled() {
    return this.appService.app?.canUseFeature('team-self-serve-ai-onboarding') ?? false;
  }

  activate() {
    if (!this.isNewOperatorEnabled) {
      return;
    }

    this._unsubscribeKnowledgeHub = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'knowledge-hub:open-content',
      (data) => {
        let editorMode = data.editorMode || 'view';
        if (editorMode === 'view') {
          this.knowledgeHubDrawerEditorService.openViewDrawer({
            activeContentId: data.contentId,
            activeContentType: data.contentType,
            newTab: false,
          });
        } else {
          this.knowledgeHubDrawerEditorService.openEditDrawer({
            activeContentId: data.contentId,
            activeContentType: data.contentType,
            newTab: false,
          });
        }
      },
    );

    this._unsubscribeNavigate = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'internal-agent:navigate',
      (data) => {
        switch (data.entityType) {
          case EntityType.Guideline:
            this.router.transitionTo('apps.app.automation.fin-ai-agent.guidance');
            break;
          case EntityType.Procedure:
            this.router.transitionTo(
              'apps.app.automation.fin-ai-agent.procedures.procedure',
              data.entityId,
            );
            break;
          case EntityType.Workflow:
            this.router.transitionTo('apps.app.automation.workflows.edit', data.entityId, {
              queryParams: { visual: 'enabled', step_id: data.stepId ?? null },
            });
            break;
          case EntityType.Conversation:
            safeWindowOpen(
              this.router.urlFor('apps.app.inbox.inbox.conversation', data.entityId),
              '_blank',
            );
            break;
        }
      },
    );
  }

  deactivate() {
    if (this._unsubscribeKnowledgeHub) {
      this._unsubscribeKnowledgeHub();
      this._unsubscribeKnowledgeHub = undefined;
    }
    if (this._unsubscribeNavigate) {
      this._unsubscribeNavigate();
      this._unsubscribeNavigate = undefined;
    }
  }

  buildRouteInfoMetadata() {
    return {
      responsibleTeam: 'team-automation-foundations',
    };
  }

  async model() {
    if (this.isNewOperatorEnabled) {
      return {};
    }

    this.contentImportService.fetchContentIngestionState();
    this.contentImportService.fetchAiContentLibrarySummary();
    this.contentImportService.fetchContentReviewSummary();

    let counts = {};

    // We don't want to fail the page load in case this ever goes wrong
    try {
      let { custom_bots: customBots, answers } = await get(
        `/ember/operator_workflows/workflow_counts`,
        {
          app_id: this.app.id,
        },
      );
      counts = {
        customBots,
        answers,
      };
    } catch (e) {
      console.error(e);
    }

    return hash({
      lookAndFeel: this.store.findRecord('messenger-settings/look-and-feel', this.app.id),
      availability: this.store.findRecord('messenger-settings/availability', this.app.id),
      counts,
    });
  }

  setupController(controller, model) {
    if (this.isNewOperatorEnabled) {
      return;
    }
    super.setupController(...arguments);
    controller.initializeConfigurationModel();
  }
}
